import { DriverSettings, DEFAULT_DRIVER_SETTINGS } from '../vehicle/MirrorsAndCameras';
import { KeyMap, DEFAULT_KEY_MAP, sanitizeKeyMap } from '../input/DesktopInput';

export interface UserProfile {
  username: string;
  driverName: string;
  preferredTransmission: 'automatic' | 'manual';
  useGyro: boolean;
  examinerVoiceEnabled: boolean;
}

export interface SimulatorSettings {
  username: string;
  driverName: string;
  preferredTransmission: 'automatic' | 'manual';
  useGyro: boolean;
  examinerVoiceEnabled: boolean;
  driverSettings: DriverSettings;
  keyMap: KeyMap;
  touchPreferences?: Record<string, any>;
}

export { sanitizeKeyMap };

export function cloneKeyMap(keyMap: KeyMap): KeyMap {
  return sanitizeKeyMap(keyMap);
}

export const DEFAULT_SETTINGS: SimulatorSettings = {
  username: 'Fahrschüler',
  driverName: 'Fahrschüler',
  preferredTransmission: 'automatic',
  useGyro: false,
  examinerVoiceEnabled: false,
  driverSettings: { ...DEFAULT_DRIVER_SETTINGS },
  keyMap: cloneKeyMap(DEFAULT_KEY_MAP),
  touchPreferences: {},
};

export class SettingsSyncService {
  private static instance: SettingsSyncService;
  private currentSettings: SimulatorSettings = {
    ...DEFAULT_SETTINGS,
    driverSettings: { ...DEFAULT_DRIVER_SETTINGS },
    keyMap: cloneKeyMap(DEFAULT_KEY_MAP),
  };
  private listeners: Set<(settings: SimulatorSettings) => void> = new Set();
  private saveDebounceTimer: number | null = null;

  private constructor() {
    this.loadFromLocalStorage();
  }

  public static getInstance(): SettingsSyncService {
    if (!SettingsSyncService.instance) {
      SettingsSyncService.instance = new SettingsSyncService();
    }
    return SettingsSyncService.instance;
  }

  public getSettings(): SimulatorSettings {
    return {
      ...this.currentSettings,
      driverSettings: { ...this.currentSettings.driverSettings },
      keyMap: cloneKeyMap(this.currentSettings.keyMap),
    };
  }

  public onChange(listener: (settings: SimulatorSettings) => void) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    const snapshot = this.getSettings();
    for (const listener of this.listeners) {
      try {
        listener(snapshot);
      } catch (err) {
        console.error('Error in SettingsSync listener:', err);
      }
    }
  }

  /**
   * Load local settings immediately for zero latency startup
   */
  public loadFromLocalStorage(): SimulatorSettings {
    try {
      if (typeof localStorage === 'undefined') return this.currentSettings;

      const savedProfile = localStorage.getItem('fahrschule_profile');
      if (savedProfile) {
        const p = JSON.parse(savedProfile);
        this.currentSettings.username = p.username || p.driverName || this.currentSettings.username;
        this.currentSettings.driverName = p.driverName || p.username || this.currentSettings.driverName;
        this.currentSettings.preferredTransmission = p.preferredTransmission || this.currentSettings.preferredTransmission;
        this.currentSettings.useGyro = p.useGyro ?? this.currentSettings.useGyro;
        this.currentSettings.examinerVoiceEnabled = p.examinerVoiceEnabled ?? this.currentSettings.examinerVoiceEnabled;
      }

      const savedDriver = localStorage.getItem('fahrschule_driver_settings');
      if (savedDriver) {
        this.currentSettings.driverSettings = { ...DEFAULT_DRIVER_SETTINGS, ...JSON.parse(savedDriver) };
      }

      const savedKeyMap = localStorage.getItem('fahrschule_keymap');
      if (savedKeyMap) {
        const parsedKeyMap = JSON.parse(savedKeyMap);
        const sanitized = sanitizeKeyMap(parsedKeyMap);
        this.currentSettings.keyMap = sanitized;
        // If local storage contained corrupted bindings, immediately heal it
        if (JSON.stringify(parsedKeyMap) !== JSON.stringify(sanitized)) {
          this.saveToLocalStorage();
          this.scheduleDatabaseSync();
        }
      }
    } catch (err) {
      console.warn('Could not read settings from localStorage:', err);
    }
    return this.currentSettings;
  }

  /**
   * Asynchronously fetches settings from the backend database and merges them
   */
  public async syncFromDatabase(username?: string): Promise<SimulatorSettings> {
    const user = username || this.currentSettings.username || 'Fahrschüler';
    try {
      if (typeof fetch === 'undefined') return this.currentSettings;

      const res = await fetch(`/api/settings/${encodeURIComponent(user)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.settings) {
          const s = data.settings;
          this.currentSettings.username = s.username || this.currentSettings.username;
          this.currentSettings.driverName = s.driverName || this.currentSettings.driverName;
          this.currentSettings.preferredTransmission = s.preferredTransmission || this.currentSettings.preferredTransmission;
          this.currentSettings.useGyro = s.useGyro ?? this.currentSettings.useGyro;
          this.currentSettings.examinerVoiceEnabled = s.examinerVoiceEnabled ?? this.currentSettings.examinerVoiceEnabled;

          if (s.driverSettings) {
            this.currentSettings.driverSettings = {
              ...DEFAULT_DRIVER_SETTINGS,
              ...s.driverSettings,
            };
          }

          if (s.keyMap && typeof s.keyMap === 'object') {
            const sanitized = sanitizeKeyMap(s.keyMap);
            const hadCorruption = JSON.stringify(s.keyMap) !== JSON.stringify(sanitized);
            this.currentSettings.keyMap = sanitized;
            if (hadCorruption) {
              this.scheduleDatabaseSync();
            }
          }

          // Persist back to LocalStorage
          this.saveToLocalStorage();
          this.notifyListeners();
        }
      }
    } catch (err) {
      console.warn('Backend database sync offline or unreachable, using local preferences:', (err as Error).message);
    }
    return this.currentSettings;
  }

  /**
   * Updates partial settings, writes to localStorage, and schedules DB sync
   */
  public updateSettings(partial: Partial<SimulatorSettings>) {
    this.currentSettings = {
      ...this.currentSettings,
      ...partial,
      driverSettings: {
        ...this.currentSettings.driverSettings,
        ...(partial.driverSettings || {}),
      },
      keyMap: partial.keyMap
        ? cloneKeyMap({ ...this.currentSettings.keyMap, ...partial.keyMap })
        : this.currentSettings.keyMap,
    };

    if (partial.driverName && !partial.username) {
      this.currentSettings.username = partial.driverName;
    }

    this.saveToLocalStorage();
    this.notifyListeners();
    this.scheduleDatabaseSync();
  }

  public updateDriverSettings(driverSettings: Partial<DriverSettings>) {
    this.updateSettings({
      driverSettings: {
        ...this.currentSettings.driverSettings,
        ...driverSettings,
      },
    });
  }

  private saveToLocalStorage() {
    try {
      if (typeof localStorage === 'undefined') return;

      localStorage.setItem(
        'fahrschule_profile',
        JSON.stringify({
          username: this.currentSettings.username,
          driverName: this.currentSettings.driverName,
          preferredTransmission: this.currentSettings.preferredTransmission,
          useGyro: this.currentSettings.useGyro,
          examinerVoiceEnabled: this.currentSettings.examinerVoiceEnabled,
        })
      );

      localStorage.setItem(
        'fahrschule_driver_settings',
        JSON.stringify(this.currentSettings.driverSettings)
      );

      localStorage.setItem(
        'fahrschule_keymap',
        JSON.stringify(this.currentSettings.keyMap)
      );
    } catch (err) {
      console.warn('Could not save settings to localStorage:', err);
    }
  }

  private scheduleDatabaseSync() {
    if (typeof window === 'undefined' || typeof fetch === 'undefined') return;

    if (this.saveDebounceTimer !== null) {
      clearTimeout(this.saveDebounceTimer);
    }

    this.saveDebounceTimer = window.setTimeout(async () => {
      this.saveDebounceTimer = null;
      try {
        await fetch('/api/settings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: this.currentSettings.username,
            driverName: this.currentSettings.driverName,
            preferredTransmission: this.currentSettings.preferredTransmission,
            useGyro: this.currentSettings.useGyro,
            examinerVoiceEnabled: this.currentSettings.examinerVoiceEnabled,
            driverSettings: this.currentSettings.driverSettings,
            keyMap: this.currentSettings.keyMap,
            touchPreferences: this.currentSettings.touchPreferences,
          }),
        });
      } catch (err) {
        console.warn('Asynchronous database settings sync failed (offline):', (err as Error).message);
      }
    }, 500);
  }
}
